import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'rsuite';
import { AlertWithTimestamp } from '../../../../../../components/AlertWithTimestamp';
import { extractApiErrorMessage } from '../../../../../../utils/extractApiErrorMessage';
import { SetupIpaPricingResponsibilitiesForm } from '../schema';
import { getPricerAssignments } from '../service';
import { PricerAssignment, PricingScopeLevel } from '../types';
import {
  useCreatePricerAssignmentMutation,
  useCreatePricingScopeLevelMutation,
  useDeletePricerAssignmentMutation,
  usePricerAssignmentsQuery,
  usePricingScopeCategoryLevelsQuery,
  usePricingScopeLevelQuery,
  useUpdatePricerAssignmentMutation,
  useUpdatePricingScopeLevelMutation,
} from './usePricingScopeQueries';

const PAGE_SIZE = 20;
const DEFAULT_SORT = 'userEmail,asc';

export type AssignmentModalState = { mode: 'create'; assignment: null } | { mode: 'edit'; assignment: PricerAssignment } | null;

const showError = (error: unknown, fallback: string) => {
  AlertWithTimestamp({ message: extractApiErrorMessage(error) ?? fallback });
};

export const usePricingScopeController = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isClearingSearch, setIsClearingSearch] = useState(false);
  const [page, setPage] = useState(0);
  const [pendingLevel, setPendingLevel] = useState<PricingScopeLevel | null>(null);
  const [assignmentModal, setAssignmentModal] = useState<AssignmentModalState>(null);
  const [assignmentToRemove, setAssignmentToRemove] = useState<PricerAssignment | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const levelQuery = usePricingScopeLevelQuery();
  const categoryLevelsQuery = usePricingScopeCategoryLevelsQuery();
  const currentLevel = levelQuery.data;
  const assignmentsParams = useMemo(() => ({ search: debouncedSearch || undefined, page, size: PAGE_SIZE, sort: DEFAULT_SORT }), [debouncedSearch, page]);
  const assignmentsQuery = usePricerAssignmentsQuery(assignmentsParams, currentLevel != null);
  const assignmentCountParams = useMemo(() => ({ page: 0, size: 1, sort: DEFAULT_SORT }), []);
  const assignmentCountQuery = usePricerAssignmentsQuery(assignmentCountParams, currentLevel != null);
  const assignments = useMemo(() => assignmentsQuery.data?.content ?? [], [assignmentsQuery.data?.content]);

  useEffect(() => {
    if (levelQuery.isError) showError(levelQuery.error, 'Não foi possível consultar o nível de categoria configurado.');
  }, [levelQuery.error, levelQuery.isError]);

  useEffect(() => {
    if (assignmentsQuery.isError) showError(assignmentsQuery.error, 'Não foi possível consultar as atribuições.');
  }, [assignmentsQuery.error, assignmentsQuery.isError]);

  useEffect(() => {
    if (isClearingSearch && !assignmentsQuery.isFetching && debouncedSearch === '') {
      setIsClearingSearch(false);
    }
  }, [assignmentsQuery.isFetching, debouncedSearch, isClearingSearch]);

  const createLevelMutation = useCreatePricingScopeLevelMutation();
  const updateLevelMutation = useUpdatePricingScopeLevelMutation();
  const createAssignmentMutation = useCreatePricerAssignmentMutation();
  const updateAssignmentMutation = useUpdatePricerAssignmentMutation();
  const deleteAssignmentMutation = useDeletePricerAssignmentMutation();

  const levelOptions = useMemo(
    () =>
      (categoryLevelsQuery.data ?? []).map(level => ({
        label: `${level.level} - ${level.label}`,
        value: level.level,
        name: level.label,
      })),
    [categoryLevelsQuery.data],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
    setIsClearingSearch(value === '' && search !== '');
  };

  const persistLevelWithoutAssignments = async (level: PricingScopeLevel) => {
    try {
      await createLevelMutation.mutateAsync(level);
      Alert.success('Nível de categoria configurado com sucesso.');
    } catch (error) {
      showError(error, 'Não foi possível configurar o nível de categoria.');
    }
  };

  const handleLevelChange = async (levelNumber: number | null) => {
    if (levelNumber == null || levelNumber === currentLevel?.level) return;

    if (currentLevel === undefined || levelQuery.isLoading || levelQuery.isError) return;

    if (currentLevel && (assignmentCountQuery.isLoading || assignmentCountQuery.isFetching)) return;

    if (currentLevel && (assignmentCountQuery.isError || !assignmentCountQuery.data)) {
      showError(assignmentCountQuery.error, 'Não foi possível confirmar se existem atribuições. Tente novamente.');
      return;
    }

    const selectedOption = levelOptions.find(option => option.value === levelNumber);
    if (!selectedOption) return;

    const nextLevel = { level: selectedOption.value, name: selectedOption.name };
    if (currentLevel && (assignmentCountQuery.data?.totalElements ?? 0) > 0) {
      setPendingLevel(nextLevel);
      return;
    }

    await persistLevelWithoutAssignments(nextLevel);
  };

  const confirmLevelChange = async () => {
    if (!pendingLevel) return;

    try {
      const result = await updateLevelMutation.mutateAsync(pendingLevel);
      setPendingLevel(null);
      setPage(0);
      Alert.success(
        `Nível de categoria alterado com sucesso. ${result?.deletedAssignmentsCount ?? assignmentCountQuery.data?.totalElements ?? 0} atribuições removidas.`,
      );
    } catch (error) {
      showError(error, 'Não foi possível trocar o nível de categoria.');
    }
  };

  const openCreateAssignment = () => setAssignmentModal({ mode: 'create', assignment: null });
  const openEditAssignment = (assignment: PricerAssignment) => setAssignmentModal({ mode: 'edit', assignment });
  const closeAssignmentModal = () => setAssignmentModal(null);

  const saveAssignment = async (values: SetupIpaPricingResponsibilitiesForm) => {
    const payload = {
      cdUsuario: values.cdUsuario,
      userName: values.userName,
      userEmail: values.userEmail,
      categories: values.categories,
    };

    try {
      if (assignmentModal?.mode === 'edit') {
        await updateAssignmentMutation.mutateAsync({ id: assignmentModal.assignment.id, payload });
        Alert.success('Atribuição atualizada com sucesso.');
      } else {
        await createAssignmentMutation.mutateAsync(payload);
        Alert.success('Atribuição criada com sucesso.');
      }
      setAssignmentModal(null);
    } catch (error) {
      if (error?.response?.status !== 409) {
        showError(error, assignmentModal?.mode === 'edit' ? 'Não foi possível atualizar a atribuição.' : 'Não foi possível criar a atribuição.');
      }
      throw error;
    }
  };

  const editExistingAssignmentForUser = async (cdUsuario: number, userEmail: string) => {
    let existingAssignment = assignments.find(assignment => assignment.cdUsuario === cdUsuario);

    if (!existingAssignment) {
      try {
        const response = await getPricerAssignments({ search: userEmail, page: 0, size: PAGE_SIZE, sort: DEFAULT_SORT });
        existingAssignment = response.content.find(assignment => assignment.cdUsuario === cdUsuario);
      } catch (error) {
        showError(error, 'Não foi possível localizar a atribuição existente.');
        return;
      }
    }

    if (existingAssignment) {
      openEditAssignment(existingAssignment);
    } else {
      showError(null, 'A atribuição existente não foi encontrada. Atualize a lista e tente novamente.');
    }
  };

  const removeAssignment = async () => {
    if (!assignmentToRemove) return;

    try {
      await deleteAssignmentMutation.mutateAsync(assignmentToRemove.id);
      setAssignmentToRemove(null);
      Alert.success('Atribuição removida com sucesso.');
    } catch (error) {
      showError(error, 'Não foi possível remover a atribuição.');
    }
  };

  return {
    data: {
      currentLevel,
      levelOptions,
      assignments,
      assignmentsPage: assignmentsQuery.data,
      assignmentCount: assignmentCountQuery.data?.totalElements,
    },
    state: {
      search,
      debouncedSearch,
      isClearingSearch,
      page,
      pendingLevel,
      assignmentModal,
      assignmentToRemove,
    },
    loading: {
      level: levelQuery.isLoading,
      categoryLevels: categoryLevelsQuery.isLoading,
      assignments: assignmentsQuery.isLoading || assignmentsQuery.isFetching,
      assignmentCount: assignmentCountQuery.isLoading || assignmentCountQuery.isFetching,
      savingLevel: createLevelMutation.isLoading || updateLevelMutation.isLoading,
      savingAssignment: createAssignmentMutation.isLoading || updateAssignmentMutation.isLoading,
      removingAssignment: deleteAssignmentMutation.isLoading,
    },
    handlers: {
      handleSearch,
      setPage,
      handleLevelChange,
      confirmLevelChange,
      cancelLevelChange: () => setPendingLevel(null),
      openCreateAssignment,
      openEditAssignment,
      closeAssignmentModal,
      saveAssignment,
      editExistingAssignmentForUser,
      setAssignmentToRemove,
      removeAssignment,
    },
  };
};
